import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "src/components/ui/Button";
import { useAuth } from "src/context/AuthContext";
import {
  Menu,
  X,
  MessageSquare,
  LayoutDashboard,
  Shield,
  LogIn,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Code2,
  FileJson,
  Monitor,
} from "lucide-react";
import { useNavbarStore } from "src/store/navbarStore";
import { ROUTES } from "src/routes/RouteConfig";
import UserProfileMenu from "src/components/UserProfileMenu";
import { cn } from "src/lib/utils";

// Add isAdminPage prop with default value
interface NavbarProps {
  isAdminPage?: boolean;
}

type NavLinkItem = {
  type: "link";
  path: string;
  label: string;
};

type NavGroupItem = {
  type: "group";
  label: string;
  children: Array<NavLinkItem | NavGroupItem>;
};

type MainNavItem = NavLinkItem | NavGroupItem;

const mainNavItems: MainNavItem[] = [
  { type: "link", path: ROUTES.HOME.path, label: "Home" },
  {
    type: "group",
    label: "Courses",
    children: [
      { type: "link", path: ROUTES.COURSE_MATHEMATICS.path, label: "Mathematics" },
      {
        type: "group",
        label: "Computer Science",
        children: [
          { type: "link", path: ROUTES.COURSE_PYTHON.path, label: "Python" },
          { type: "link", path: ROUTES.COURSE_JAVASCRIPT.path, label: "JavaScript" },
        ],
      },
    ],
  },
  { type: "link", path: ROUTES.TUTORS.path, label: "Tutors" },
  { type: "link", path: ROUTES.PRICING.path, label: "Pricing" },
  { type: "link", path: ROUTES.HOW_IT_WORKS.path, label: "How it Works" },
];

const collectNavPaths = (item: MainNavItem): string[] => {
  if (item.type === "link") {
    return [item.path];
  }
  return item.children.flatMap(collectNavPaths);
};

const courseIcons: Record<string, ReactNode> = {
  Mathematics: <BookOpen className="h-4 w-4" strokeWidth={1.75} />,
  "Computer Science": <Monitor className="h-4 w-4" strokeWidth={1.75} />,
  Python: <Code2 className="h-4 w-4" strokeWidth={1.75} />,
  JavaScript: <FileJson className="h-4 w-4" strokeWidth={1.75} />,
};

const menuPanelClass =
  "rounded-xl border border-[#e4d9ff] bg-white p-2 shadow-[0_10px_28px_-12px_rgba(110,89,165,0.45)]";

const menuRowClass = (active: boolean) =>
  cn(
    "flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 font-roboto outline-none transition-colors",
    "hover:bg-[#f5f3ff] focus-visible:ring-2 focus-visible:ring-[#9548ef] focus-visible:ring-offset-1",
    active && "bg-[#f5f3ff]"
  );

const CourseMenuIcon = ({ title }: { title: string }) => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#f0e9ff] text-[#6e59a5]">
    {courseIcons[title] ?? <BookOpen className="h-4 w-4" strokeWidth={1.75} />}
  </span>
);

const CourseMenuLinks = ({
  item,
  isActivePath,
  onNavigate,
  variant = "flyout",
  openGroups = [],
  onToggleGroup,
  openSubmenu,
  onOpenSubmenu,
}: {
  item: NavGroupItem;
  isActivePath: (path: string) => boolean;
  onNavigate?: () => void;
  variant?: "flyout" | "accordion";
  openGroups?: string[];
  onToggleGroup?: (label: string) => void;
  openSubmenu?: string | null;
  onOpenSubmenu?: (label: string | null) => void;
}) => {
  return (
    <div className="flex flex-col gap-1" role="menu">
      {item.children.map((child) => {
        if (child.type === "link") {
          const active = isActivePath(child.path);
          return (
            <Link
              key={child.path}
              to={child.path}
              role="menuitem"
              onClick={onNavigate}
              className={menuRowClass(active)}
            >
              <CourseMenuIcon title={child.label} />
              <span className="min-w-0 flex-1 text-left text-base font-medium leading-5 text-purple-900">
                {child.label}
              </span>
              {active ? (
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff7f52]" />
              ) : null}
            </Link>
          );
        }

        const groupActive = collectNavPaths(child).some((path) => isActivePath(path));
        const accordionOpen = openGroups.includes(child.label);

        if (variant === "accordion") {
          return (
            <div key={child.label}>
              <button
                type="button"
                className={menuRowClass(groupActive)}
                aria-haspopup="menu"
                aria-expanded={accordionOpen}
                onClick={() => onToggleGroup?.(child.label)}
              >
                <CourseMenuIcon title={child.label} />
                <span className="min-w-0 flex-1 text-left text-base font-medium leading-5 text-purple-900">
                  {child.label}
                </span>
                {groupActive ? (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff7f52]" />
                ) : null}
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 text-[#6e59a5] transition-transform",
                    accordionOpen && "rotate-180"
                  )}
                />
              </button>
              {accordionOpen ? (
                <div className="mt-1 ml-3 border-l border-[#e4d9ff] pl-2">
                  <CourseMenuLinks
                    item={child}
                    isActivePath={isActivePath}
                    onNavigate={onNavigate}
                    variant="accordion"
                    openGroups={openGroups}
                    onToggleGroup={onToggleGroup}
                  />
                </div>
              ) : null}
            </div>
          );
        }

        const submenuOpen = openSubmenu === child.label;

        return (
          <div
            key={child.label}
            className="relative"
            onMouseEnter={() => onOpenSubmenu?.(child.label)}
          >
            <button
              type="button"
              className={menuRowClass(groupActive || submenuOpen)}
              aria-haspopup="menu"
              aria-expanded={submenuOpen}
            >
              <CourseMenuIcon title={child.label} />
              <span className="min-w-0 flex-1 text-left text-base font-medium leading-5 text-purple-900">
                {child.label}
              </span>
              {groupActive ? (
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff7f52]" />
              ) : null}
              <ChevronRight className="h-4 w-4 shrink-0 text-[#6e59a5]" />
            </button>
            {submenuOpen ? (
              <div className="absolute left-full top-0 z-50 w-[17.5rem] pl-2">
                <div className={menuPanelClass}>
                  <CourseMenuLinks
                    item={child}
                    isActivePath={isActivePath}
                    onNavigate={onNavigate}
                    variant="flyout"
                  />
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const adminNavItems = [
  {
    path: ROUTES.ADMIN_DASHBOARD.path,
    label: "Dashboard",
    icon: <LayoutDashboard size={18} className="mr-1" />,
  },
  {
    path: ROUTES.ADMIN_INQUIRIES.path,
    label: "Inquiries",
    icon: <MessageSquare size={18} className="mr-1" />,
  },
];

const Navbar = ({ isAdminPage = false }: NavbarProps) => {
  const { isMenuOpen, toggleMenu, setIsMenuOpen } = useNavbarStore();
  const { user, isUserAdmin, userType } = useAuth();
  const location = useLocation();
  const [openMobileGroups, setOpenMobileGroups] = useState<string[]>([]);
  const [desktopCoursesOpen, setDesktopCoursesOpen] = useState(false);
  const [desktopSubmenu, setDesktopSubmenu] = useState<string | null>(null);

  // Function to get the correct dashboard path based on user type
  const getDashboardPath = () => {
    if (isUserAdmin()) {
      return ROUTES.ADMIN_DASHBOARD.path;
    } else if (userType === "student") {
      return ROUTES.STUDENT_DASHBOARD.path;
    } else if (userType === "tutor") {
      return ROUTES.TUTOR_DASHBOARD.path;
    }
    return "/"; // fallback
  };

  const isActivePath = (path: string): boolean => {
    if (path === ROUTES.HOME.path) {
      return location.pathname === ROUTES.HOME.path;
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (item: NavGroupItem): boolean => {
    return collectNavPaths(item).some((path) => isActivePath(path));
  };

  const toggleMobileGroup = (label: string) => {
    setOpenMobileGroups((current) =>
      current.includes(label)
        ? current.filter((item) => item !== label)
        : [...current, label]
    );
  };

  return (
    <nav
      className={`sticky top-5 z-50 ${
        isAdminPage ? "bg-rfpurple-50/95" : "bg-[#f0e9ff]/75"
      } backdrop-blur-sm rounded-lg mx-2 sm:mx-4 md:mx-9 mt-5`}
    >
      <div className="w-full px-2">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Left side - Logo */}
          <div className="flex-shrink-0 ml-2">
            <Link
              to={ROUTES.HOME.path}
              className="flex items-center focus:outline-none focus:ring-0 border-0 no-underline"
              style={{ border: 'none', outline: 'none' }}
            >
              <div className="flex items-center gap-1">
                <img
                  src="/logo.png"
                  alt="rfLearn logo"
                  className="h-8 w-auto object-contain max-w-[200px] border-0"
                  style={{ border: 'none', outline: 'none' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden items-center gap-1">
                  <span
                    className="text-rfpurple-600 text-2xl md:text-3xl font-bold"
                    style={{ fontFamily: "'Comic Sans MS', cursive" }}
                  >
                    rf
                  </span>
                  <span
                    className="text-2xl md:text-3xl font-bold text-[#ff7f52]"
                    style={{ fontFamily: "'Comic Sans MS', cursive" }}
                  >
                    Learn
                  </span>
                </div>
              </div>
              {isAdminPage && (
                <span className="ml-2 bg-rfpurple-100 text-rfpurple-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
                  <Shield size={12} className="mr-1" />
                  Admin
                </span>
              )}
            </Link>
          </div>

          {/* Center - Navigation Items */}
          <div className="hidden lg:flex lg:items-center lg:justify-center lg:flex-1 lg:space-x-8">
            {isAdminPage ? (
              // Admin-specific navigation items
              <>
                {adminNavItems.map((item) => (
                  <div
                    key={item.path}
                    className="relative flex flex-col items-center"
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center hover:text-rfpurple-600 px-3 py-2 rounded-md text-base tracking-wide font-roboto ${
                        isActivePath(item.path)
                          ? "text-purple-900 font-medium"
                          : "text-gray-700 font-normal"
                      }`}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                    {isActivePath(item.path) && (
                      <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#ff7f52] bg-opacity-80"></div>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <>
                {mainNavItems.map((item) =>
                  item.type === "link" ? (
                    <div
                      key={item.path}
                      className="relative flex flex-col items-center"
                    >
                      <Link
                        to={item.path}
                        className={`flex items-center hover:text-rfpurple-600 px-3 py-2 rounded-md text-base tracking-wide font-roboto ${
                          isActivePath(item.path)
                            ? "text-purple-900 font-medium"
                            : "text-purple-900 font-normal"
                        }`}
                      >
                        {item.label}
                      </Link>
                      {isActivePath(item.path) && (
                        <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#ff7f52] bg-opacity-80"></div>
                      )}
                    </div>
                  ) : (
                    <div
                      key={item.label}
                      className="relative flex flex-col items-center"
                      onMouseEnter={() => setDesktopCoursesOpen(true)}
                      onMouseLeave={() => {
                        setDesktopCoursesOpen(false);
                        setDesktopSubmenu(null);
                      }}
                    >
                      <button
                        type="button"
                        className={`flex items-center hover:text-rfpurple-600 px-3 py-2 rounded-md text-base tracking-wide font-roboto ${
                          isGroupActive(item)
                            ? "text-purple-900 font-medium"
                            : "text-purple-900 font-normal"
                        }`}
                        aria-haspopup="menu"
                        aria-expanded={desktopCoursesOpen}
                        onClick={() => setDesktopCoursesOpen(true)}
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            "ml-1 h-4 w-4 transition-transform duration-200",
                            desktopCoursesOpen && "rotate-180"
                          )}
                        />
                      </button>
                      {isGroupActive(item) && (
                        <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#ff7f52] bg-opacity-80"></div>
                      )}
                      {desktopCoursesOpen ? (
                        <div className="absolute left-1/2 top-full z-50 w-[17.5rem] -translate-x-1/2 pt-3">
                          <div className={menuPanelClass}>
                            <CourseMenuLinks
                              item={item}
                              isActivePath={isActivePath}
                              variant="flyout"
                              openSubmenu={desktopSubmenu}
                              onOpenSubmenu={setDesktopSubmenu}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                )}
              </>
            )}
          </div>

          {/* Right side - auth buttons (ONLY VISIBLE ON LARGE SCREENS) */}
          <div className="hidden lg:flex items-center flex-shrink-0 mr-2">
            {user ? (
              <UserProfileMenu
                isAdmin={isUserAdmin()}
                isAdminPage={isAdminPage}
              />
            ) : (
              // Desktop login/signup buttons
              <div className="flex items-center space-x-1">
                <Button
                  className="bg-transparent border-0 text-[#ff7f52] hover:bg-[#ff7f52] hover:text-white px-4"
                  asChild
                >
                  <Link to={ROUTES.BOOK_DEMO.path}>Book a Free Demo</Link>
                </Button>
                <Button
                  className="bg-transparent border-2 border-[#9548ef] text-[#9548ef] hover:bg-[#9548ef] hover:text-white px-6"
                  asChild
                >
                  <Link to={ROUTES.LOGIN.path}>Login</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button - VISIBLE ON MOBILE AND MEDIUM DEVICES */}
          <div className="flex lg:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-rfpurple-500"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">
                {isMenuOpen ? "Close main menu" : "Open main menu"}
              </span>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - use isMenuOpen from Zustand */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 animate-fade-in">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {isAdminPage ? (
              // Admin-specific mobile navigation
              <>
                {adminNavItems.map((item) => (
                  <div key={item.path} className="relative">
                    <Link
                      to={item.path}
                      className={`flex items-center hover:bg-gray-50 px-3 py-2 rounded-md text-base font-roboto ${
                        isActivePath(item.path)
                          ? "text-purple-900 font-medium"
                          : "text-gray-700 font-normal"
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.icon}
                      {item.label}
                      {isActivePath(item.path) && (
                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#ff7f52] bg-opacity-80"></div>
                      )}
                    </Link>
                  </div>
                ))}
              </>
            ) : (
              <>
                {mainNavItems.map((item) =>
                  item.type === "link" ? (
                    <div key={item.path} className="relative">
                      <Link
                        to={item.path}
                        className={`flex items-center hover:bg-gray-50 px-3 py-2 rounded-md text-base font-roboto ${
                          isActivePath(item.path)
                            ? "text-purple-900 font-medium"
                            : "text-gray-700 font-normal"
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                        {isActivePath(item.path) && (
                          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#ff7f52] bg-opacity-80"></div>
                        )}
                      </Link>
                    </div>
                  ) : (
                    <div key={item.label} className="relative">
                      <button
                        type="button"
                        className={`flex w-full items-center justify-between hover:bg-gray-50 px-3 py-2 rounded-md text-base font-roboto ${
                          isGroupActive(item)
                            ? "text-purple-900 font-medium"
                            : "text-gray-700 font-normal"
                        }`}
                        aria-expanded={openMobileGroups.includes(item.label)}
                        onClick={() => toggleMobileGroup(item.label)}
                      >
                        <span className="flex items-center">
                          {item.label}
                          {isGroupActive(item) && (
                            <span className="ml-2 h-1.5 w-1.5 rounded-full bg-[#ff7f52] bg-opacity-80"></span>
                          )}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            openMobileGroups.includes(item.label) ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {openMobileGroups.includes(item.label) && (
                        <div className="mx-1 mb-1 mt-1 rounded-xl border border-[#e4d9ff] bg-white p-1.5">
                          <CourseMenuLinks
                            item={item}
                            isActivePath={isActivePath}
                            onNavigate={() => setIsMenuOpen(false)}
                            variant="accordion"
                            openGroups={openMobileGroups}
                            onToggleGroup={toggleMobileGroup}
                          />
                        </div>
                      )}
                    </div>
                  )
                )}

                {isUserAdmin() && !isAdminPage && (
                  <Link
                    to={ROUTES.ADMIN_DASHBOARD.path}
                    className="flex items-center text-gray-700 hover:text-rfpurple-600 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Shield size={18} className="mr-2" />
                    Admin Dashboard
                  </Link>
                )}
              </>
            )}

            {user && !isAdminPage && (
              <Link
                to={getDashboardPath()}
                className="flex items-center text-gray-700 hover:text-rfpurple-600 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                <LayoutDashboard size={18} className="mr-2" />
                Dashboard
              </Link>
            )}

            <div className="flex flex-col space-y-2 pt-2">
              {user ? (
                <div className="w-full">
                  <UserProfileMenu
                    isAdmin={isUserAdmin()}
                    isAdminPage={isAdminPage}
                    className="w-full justify-start"
                  />
                </div>
              ) : (
                <>
                  <Button
                    className="w-full bg-transparent border-2 border-[#ff7f52] text-[#ff7f52] hover:bg-[#ff7f52] hover:text-white"
                    asChild
                  >
                    <Link
                      to={ROUTES.BOOK_DEMO.path}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LogIn size={18} className="mr-2" />
                      Book a Free Demo
                    </Link>
                  </Button>
                  <Button
                    className="w-full bg-transparent border-2 border-[#9548ef] text-[#9548ef] hover:bg-[#9548ef] hover:text-white"
                    asChild
                  >
                    <Link
                      to={ROUTES.LOGIN.path}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Login
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
